import React, { useEffect } from "react";
import type { SymptomDaySummary } from "../utils/symptomUtils";
import {
  getBleedingBgColor,
  getBleedingColourLabel,
  capitalizeFlow,
  capitalizeClots,
} from "../utils/bleedingUtils";

type BleedingLogModalProps = {
  day: SymptomDaySummary;
  onClose: () => void;
};

export const BleedingLogModal: React.FC<BleedingLogModalProps> = ({ day, onClose }) => {
  const bleeding = day.log?.symptoms?.vaginalBleeding;

  // Detect if this entry represents a logged "No Bleeding" day
  const isNoBleeding = bleeding && !bleeding.colour;

  // Set the circle color to healthy green if there's no bleeding
  const bgColor = isNoBleeding ? "#D1E0B7" : getBleedingBgColor(bleeding?.colour ?? undefined);
  const colourLabel = isNoBleeding ? "No Bleeding" : (getBleedingColourLabel(bleeding?.colour ?? undefined) || "N/A");

  // Clean up Flow and Clots strings so they display "None" instead of "None flow" or "N/A"
  const rawFlow = bleeding?.flow;
  const flowLabel = isNoBleeding || !rawFlow || rawFlow === "none" ? "None" : capitalizeFlow(rawFlow);

  const rawClots = bleeding?.clots;
  const clotsLabel = isNoBleeding || !rawClots || rawClots === "none" ? "None" : capitalizeClots(rawClots);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const parsedDate = day.dateId ? new Date(day.dateId.replace(/-/g, "/")) : null;
  const formattedDate =
    parsedDate && !isNaN(parsedDate.getTime())
      ? parsedDate.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : day.dateId ?? "Unknown";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 pointer-events-none" />

      {/* Panel */}
      <div
        className="
          pointer-events-auto relative bg-[#FCF9F7]
          rounded-t-[32px] md:rounded-[32px]
          w-full max-w-[430px]
          h-[625px] md:h-[580px]
          md:my-10 shadow-xl
          flex flex-col
          px-6 pt-[72px] pb-6
          gap-4
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          className="absolute top-6 right-6 w-6 h-6 flex items-center justify-center"
          onClick={onClose}
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 13L13 1M1 1L13 13"
              stroke="#303030"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Date Title */}
        <h2 className="absolute top-9 left-6 font-dmSans font-semibold text-[24px] leading-[31px] text-[#0C2624]">
          {formattedDate}
        </h2>

        {/* Colour Card */}
        <div className="w-full bg-[#F5F5F5] rounded-[16px] p-4 flex items-center gap-4 h-[125px] shrink-0">
          <div
            className="w-[50px] h-[50px] rounded-full shrink-0"
            style={{ backgroundColor: bgColor }}
          />
          <div className="flex flex-col">
            <span className="font-dmSans font-semibold text-[16px] text-[#0C2624]">Colour</span>
            <span className="font-dmSans font-semibold text-[20px] text-[#303030]">{colourLabel}</span>
          </div>
        </div>

        {/* Flow Card */}
        <div className="w-full bg-[#F5F5F5] rounded-[16px] p-4 flex flex-col justify-center gap-3 h-[96px] shrink-0">
          <span className="font-dmSans font-semibold text-[16px] text-[#0C2624]">Flow</span>
          <span className="font-dmSans font-semibold text-[20px] text-[#303030]">
            {flowLabel}
          </span>
        </div>

        {/* Clots Card */}
        <div className="w-full bg-[#F5F5F5] rounded-[16px] p-4 flex flex-col justify-center gap-3 h-[96px] shrink-0">
          <span className="font-dmSans font-semibold text-[16px] text-[#0C2624]">Clots</span>
          <span className="font-dmSans font-semibold text-[20px] text-[#303030]">
            {clotsLabel}
          </span>
        </div>

        {/* Submit Button (slot) */}
      </div>
    </div>
  );
};